import React, { useCallback, useContext, useEffect, useState } from 'react'
import { AdminAssessmentsStyles } from './Style'
import { useSelector } from 'react-redux'
import { dataGridModel } from 'keys'
import { LightButton, TablePageStyles, WrapHeader } from 'styles/GlobalStyle'
import { Table, Tabs } from 'components'
import { isEmpty, isEqual } from 'lodash'
import { queryClient as qc } from 'keys'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { getAdminAssessmentsFetch, getAdminAssessmentVersionsFetch } from 'store/middlewares/assessment.crud'
import { initialPage, initialPageSize } from 'keys/pagination'
import { TranscriptionSystemContext } from 'pages/Admin/TranscriptionSystem/TranscriptionSystem'
import { getTranscriptionUsersFetch } from 'store/middlewares/user.crud'
import { markingWordsHooks } from '../MarkingWords'
import { Can } from 'components/Permissions'
import { adminAssessmentsHooks, CreateVersionPopup } from '.'
import AdminAssessmentActions from './AdminAssessmentActions.tsx'

const AdminAssessments = (props) => {
    const { status, setStatus, assessmentFilters, setAssessmentFilters, popups, setPopups } = useContext(TranscriptionSystemContext)
    const { tabs = [] } = props
    const [statusFilters, setStatusFilters] = useState(status)
    const [currentTab, setCurrentTab] = useState(0)
    const [rowOpen, setRowOpen] = useState(null)
    const [page, setPage] = useState(initialPage)
    const [pageSize, setPageSize] = useState(initialPageSize)
    const [sorting, setSorting] = useState([])
    const [searchVal, setSearchVal] = useState("")
    const [selectedAssessments, setSelectedAssessments] = useState([])

    const { testTaker } = useSelector(state => state.testTaker)
    const { headerFilters } = useSelector(state => state.header)

    const isTraining = markingWordsHooks.useIsTraining()
    const actionBtns = adminAssessmentsHooks.useHeaderActionBtns(selectedAssessments, setSelectedAssessments)

    const transcriptionUsers = useQuery({
        queryKey: [qc.transcriptionUsers],
        queryFn: async () => {
            const { data } = await getTranscriptionUsersFetch()
            return data
        }
    })

    const filterSchema = {
        primary: [
            { key: "marking_words_update_by", overrides: { name: "marking_words_update_by", options: transcriptionUsers.data, placeholder: "בחר מתעתק", component: "autoComplete" } },
        ],
        advanced: [
            { key: "school_year" },
            { key: "semester" },
            { key: "grade_of_execution" },
            { key: "categories" },
            { key: "assessment_defs" }
        ]
    }

    const adminAssessmentOptions = queryOptions({
        queryKey: [qc.adminAssessments, page, pageSize, sorting, statusFilters, headerFilters, assessmentFilters, currentTab, searchVal, isTraining],
        queryFn: async ({ signal }) => {
            const params = {
                headerFilters, assessmentFilters, statusFilters, testTaker, signal, page, pageSize, sorting, search: searchVal, model: qc.adminAssessments
            }
            const { data } = await getAdminAssessmentsFetch(params, isTraining)
            return data
        }
    })

    const adminAssessments = useQuery(adminAssessmentOptions)

    const adminAssessmentVersionsOptions = queryOptions({
        queryKey: [qc.adminAssessmentVersions, rowOpen],
        queryFn: async () => {
            const { data } = await getAdminAssessmentVersionsFetch(rowOpen)
            return data
        },
        enabled: !!rowOpen
    })

    const adminAssessmentVersions = useQuery(adminAssessmentVersionsOptions)

    let defaulTabIndex

    useEffect(() => {
        if (isEmpty(statusFilters))
            defaulTabIndex = 0
        else {
            defaulTabIndex = tabs.findIndex(({ filters }) =>
                filters.workflowState ?
                    isEqual(filters.workflowState, statusFilters.workflowState) :
                    (isEqual(filters.status, statusFilters.status) && isEqual(filters.sub_status, statusFilters.sub_status)))
        }

        if (defaulTabIndex < 0)
            defaulTabIndex = 0

        const { status, sub_status, audio_status, exclude_audio_status, exclude_sub_status, workflowState } = tabs?.[defaulTabIndex]?.filters
        if (workflowState)
            setStatusFilters({
                workflowState: workflowState
            })
        else setStatusFilters({
            status: status, audio_status: audio_status, sub_status: sub_status, exclude_sub_status: exclude_sub_status, exclude_audio_status: exclude_audio_status
        })
        setCurrentTab(defaulTabIndex)
        setSorting([tabs[defaulTabIndex].sortBy])
    }, [])

    useEffect(() => {
        return (() => {
            setStatus(statusFilters)
            setPage(initialPage)
        })
    }, [statusFilters])

    // חשוב לאפס את העמוד בעת שינוי סינון
    useEffect(() => {
        setPage(initialPage)
    }, [headerFilters, assessmentFilters, statusFilters])

    const handleTab = (index) => {
        const { status, sub_status, audio_status, exclude_sub_status, exclude_audio_status, workflowState } = tabs[index].filters
        setCurrentTab(index)
        if (workflowState)
            setStatusFilters({
                workflowState: workflowState
            })
        else setStatusFilters({
            status: status, sub_status: sub_status, audio_status: audio_status, exclude_sub_status: exclude_sub_status, exclude_audio_status: exclude_audio_status
        })
        setPage(initialPage)
        setSorting([])
        setSorting([tabs[index].sortBy])
        // queryClient.resetQueries({ queryKey: [adminAssessmentOptions.queryKey], exact: true })
    }

    const onCollapse = useCallback((id, rowIndex) => {
        setRowOpen(rowIndex)
    }, [])

    return (
        <AdminAssessmentsStyles>
            <WrapHeader height={50}>
                <div className='header'>
                    <Tabs defaultValue={currentTab} tabs={tabs} handleTab={handleTab} />
                    <Can I="manage" a="super-user">
                        <div className='action-btns'>
                            {actionBtns?.map((actionBtn) =>
                                <LightButton
                                    variant="solid"
                                    size="small"
                                    {...actionBtn}
                                />
                            )}
                        </div>
                    </Can>
                </div>
            </WrapHeader>
            <TablePageStyles hideSpace>
                <Table
                    filterSchema={filterSchema}
                    filterValues={assessmentFilters}
                    onFilterChange={setAssessmentFilters}
                    withTitle
                    withTabs={true}
                    loading={adminAssessments.isFetching && !adminAssessments.data}
                    data={adminAssessments?.data}
                    columns={tabs[currentTab]?.columns || []}
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    selectedRowIds={selectedAssessments ?? {}}
                    setSelectedRowIds={setSelectedAssessments}
                    onRowSelection={setSelectedAssessments}
                    visibilityModelKey={dataGridModel.admin_assessments}
                    searchVal={searchVal}
                    setSearchVal={setSearchVal}
                    sorting={sorting}
                    setSorting={setSorting}
                    queryKey={adminAssessmentOptions.queryKey}
                    rowOpen={rowOpen}
                    setRowOpen={setRowOpen}
                    collapse={{
                        show: tabs[currentTab]?.collapseColumns,
                        columns: tabs[currentTab]?.collapseColumns,
                        showToolbar: false,
                        data: adminAssessmentVersions.data,
                        onCollapse: onCollapse,
                        showChollapseIcon: true,
                    }}
                />
            </TablePageStyles>

            <AdminAssessmentActions
                popups={popups}
                setPopups={setPopups}
            />
        </AdminAssessmentsStyles>
    )
}

export default AdminAssessments